import { z } from "zod";
import {
  DOTS_MAX,
  DOTS_MIN,
  OPTIONS_MAX,
  OPTIONS_MIN,
  OPTION_MAX,
  TITLE_MAX,
} from "./limits.js";
import type { Ballot, VoteMode } from "./types.js";

// C0/C1-kontrolltegn, linje-/avsnittsseparatorer og usynlige formattegn
// (kategori Cf: bidi-styring, zero-width, tag-tegn m.m.) er aldri gyldige i
// enlinjefelt – de kan skjule eller visuelt forvrenge tekst. ZWNJ/ZWJ
// (U+200C/U+200D) tillates fordi de inngår i normal tekst, som
// emoji-sekvenser og skript som persisk.
const FORBIDDEN_CHARS = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]|(?![\u200c\u200d])\p{Cf}/u;

const singleLineText = (max: number, feltnavn: string) =>
  z
    .string({
      required_error: `${feltnavn} mangler`,
      invalid_type_error: `${feltnavn} må være tekst`,
    })
    .max(2000, `${feltnavn} er for lang`)
    .refine((s) => !FORBIDDEN_CHARS.test(s), `${feltnavn} inneholder ugyldige tegn`)
    .transform((s) => s.normalize("NFC").replace(/\s+/g, " ").trim())
    .pipe(
      z
        .string()
        .min(1, `${feltnavn} kan ikke være tomt`)
        .max(max, `${feltnavn} kan ha maks ${max} tegn`),
    );

export const voteModeSchema = z.enum(["single", "multiple", "dots"], {
  errorMap: () => ({ message: "Ugyldig avstemningstype" }),
});

export const createVoteSchema = z
  .object({
    title: singleLineText(TITLE_MAX, "Overskriften"),
    options: z
      .array(singleLineText(OPTION_MAX, "Alternativet"), {
        required_error: "Alternativer mangler",
        invalid_type_error: "Alternativer må være en liste",
      })
      .min(OPTIONS_MIN, `Du må ha minst ${OPTIONS_MIN} alternativer`)
      .max(OPTIONS_MAX, `Du kan ha maks ${OPTIONS_MAX} alternativer`),
    mode: voteModeSchema,
    maxDots: z
      .number({ invalid_type_error: "Antall stemmer må være et tall" })
      .int("Antall stemmer må være et heltall")
      .min(DOTS_MIN, `Minst ${DOTS_MIN} stemme per deltaker`)
      .max(DOTS_MAX, `Maks ${DOTS_MAX} stemmer per deltaker`)
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    value.options.forEach((option, index) => {
      const key = option.toLocaleLowerCase("nb");
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["options", index],
          message: "Alternativene må være forskjellige",
        });
      }
      seen.add(key);
    });
    if (value.mode === "dots" && value.maxDots === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxDots"],
        message: "Velg hvor mange stemmer hver deltaker får",
      });
    }
  })
  .transform((value) =>
    value.mode === "dots" ? value : { ...value, maxDots: undefined },
  );

export type CreateVoteInput = z.output<typeof createVoteSchema>;

export interface BallotContext {
  mode: VoteMode;
  optionCount: number;
  maxDots?: number;
}

/** Bygger skjema for et svar, gitt konfigurasjonen til avstemningen. */
export function ballotSchemaFor(ctx: BallotContext): z.ZodType<Ballot> {
  const optionIndex = z
    .number({ invalid_type_error: "Ugyldig alternativ" })
    .int("Ugyldig alternativ")
    .min(0, "Ugyldig alternativ")
    .max(ctx.optionCount - 1, "Ugyldig alternativ");

  if (ctx.mode === "single") {
    return z
      .object({
        selections: z
          .array(optionIndex, { required_error: "Velg et alternativ" })
          .length(1, "Velg nøyaktig ett alternativ"),
      })
      .strict();
  }

  if (ctx.mode === "multiple") {
    return z
      .object({
        selections: z
          .array(optionIndex, { required_error: "Velg minst ett alternativ" })
          .min(1, "Velg minst ett alternativ")
          .max(ctx.optionCount, "For mange valg")
          .refine(
            (a) => new Set(a).size === a.length,
            "Samme alternativ kan ikke velges flere ganger",
          ),
      })
      .strict();
  }

  const maxDots = ctx.maxDots ?? DOTS_MAX;
  return z
    .object({
      dots: z
        .array(
          z
            .number({ invalid_type_error: "Ugyldig antall stemmer" })
            .int("Ugyldig antall stemmer")
            .min(0, "Ugyldig antall stemmer")
            .max(maxDots, `Maks ${maxDots} stemmer på ett alternativ`),
          { required_error: "Fordel stemmene dine" },
        )
        .length(ctx.optionCount, "Ugyldig svar")
        .refine((a) => {
          const sum = a.reduce((acc, n) => acc + n, 0);
          return sum >= 1 && sum <= maxDots;
        }, `Fordel mellom 1 og ${maxDots} stemmer`),
    })
    .strict();
}
